import {
  getFontCss,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DatesPickerDropdown from 'src/components/DatesPickerDropdown/DatesPickerDropdown'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Spotlight from 'src/components/Spotlight/Spotlight'
import SvgSettings from 'src/components/Svgs/SvgSettings'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import {
  useBackgroundDispatch,
  useChangedEffect,
  useCookieState,
  usePrevious,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  OrgExpenseReport,
  OrgExpenseReportConfirmDateChangePopup,
} from 'src/models/orgExpenseReports.models'
import { expenseSettingsUrl } from 'src/navigation/urls'
import {
  usePatchOrgExpenseReport,
  useQueryOrgExpenseReport,
  useQueryOrgExpenseReportCycle,
  useQueryOrgExpenseReportCycleReports,
} from 'src/queries/orgExpenseReports.queries'
import {
  ORG_EXPENSE_REPORTS_CYCLES_QK,
  ORG_EXPENSE_REPORTS_QK,
} from 'src/queries/queries.constants'
import { createSetConfirmOrgExpenseReportDateChangePopup } from 'src/redux/actions/app.actions'
import { createSetOrgExpenseReportDateUpdateConfirmed } from 'src/redux/actions/data/orgExpenseReports.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectDateUpdateRequiresConfirmation } from 'src/redux/selectors/data/orgExpenseReports.selectors'
import { css } from 'styled-components'

interface StatementSummaryHeaderProps {
  creditCard?: CreditCardListModel
  report?: OrgExpenseReport
}

const StatementSummaryHeader: React.FC<StatementSummaryHeaderProps> = ({
  report,
  ...props
}): React.ReactElement => {
  const translate = useTranslate()

  const { data: cycle } = useQueryOrgExpenseReportCycle({
    cycleId: report?.cycleId,
  })
  const { data: previousReport } = useQueryOrgExpenseReport({
    reportId: cycle?.previousReportId,
  })
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const {
    mutate: patchOrgExpenseReport,
    reset,
    error,
    isPending,
  } = usePatchOrgExpenseReport()
  const dateChangeConfirmed = useSelector(selectDateUpdateRequiresConfirmation)
  const { data: cycleHistoricalReports } = useQueryOrgExpenseReportCycleReports(
    { cycleId: report?.cycleId },
  )

  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const queryClient = useQueryClient()

  const cyclePickerDisabled = useMemo(() => {
    return (
      report?.id !== cycle?.previousReportId &&
      report?.id !== cycle?.currentReportId
    )
  }, [report, cycle])
  const reportPrevious = usePrevious(report)

  const [updatedUntilForPatch, setUpdatedUntilForPatch] = useState<
    string | undefined
  >()

  // re-fetch cycle and previous report details if report end date changes
  useChangedEffect(() => {
    if (reportPrevious && reportPrevious?.until !== report?.until) {
      if (report) {
        queryClient.invalidateQueries({
          queryKey: [
            ORG_EXPENSE_REPORTS_QK,
            ORG_EXPENSE_REPORTS_CYCLES_QK,
            report.cycleId,
          ],
        })
      }
    }
  }, [reportPrevious, report])

  // send patch request if date change is confirmed
  useChangedEffect(() => {
    if (dateChangeConfirmed && updatedUntilForPatch && report) {
      patchOrgExpenseReport({
        id: report.id,
        body: { until: updatedUntilForPatch },
      })
      dispatch(createSetOrgExpenseReportDateUpdateConfirmed(false))
      setUpdatedUntilForPatch(undefined)
      analytics?.track('rcn report cycle updated', {
        card_holder: report.accountUserId,
        cycle_type: cycle?.cycleType,
        original_cycle_date: report.until,
        updated_cycle_date: moment.utc(updatedUntilForPatch).format(),
      })
    }
  }, [dateChangeConfirmed, updatedUntilForPatch, report])

  const {
    cookieValue: checkedSecondReportCycleDate,
    updateCookieValue: setCheckedSecondReportCycleDate,
  } = useCookieState<string[]>(
    CookieNames.Expenses,
    CookieValuePaths.checkedSecondReportCycleDate,
    [],
  )

  const showCheckReportDateReminder =
    cycle !== undefined &&
    // has not been dismissed previously
    !checkedSecondReportCycleDate.find((id) => id === cycle.id) &&
    // is the second and current report
    cycleHistoricalReports?.length === 2 &&
    cycle.currentReportId === report?.id

  return (
    <>
      {' '}
      {report !== undefined ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            max-width: 45%;
          `}
        >
          <UserAvatarAndName
            textCss={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
            user={{
              id: report?.accountUserId,
              fullNameOrEmail:
                report?.accountUserFullName || report?.accountUserEmail,
              initials:
                `${report?.accountUserFirstName?.slice(0, 1)}${report?.accountUserLastName?.slice(0, 1)}` ||
                report?.accountUserEmail[0],
              avatarUrl: report?.accountUserAvatarUrl,
              hasFullName: true,
            }}
          />
          <span
            css={css`
              ${getFontCss(
                28,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-top: 12px;
              margin-bottom: 8px;
              & > span {
                padding-inline-end: 12px;
              }
            `}
          >
            <span data-testid={'accountName'}>{report?.accountName}</span>
            <span data-testid={'accountLast4Tag'}>
              <Tag
                text={report.accountLast4.toString()}
                size={TagSizes.Medium}
              />
            </span>
          </span>
          <div
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <span
              css={css`
                margin-right: 4px;
              `}
            >
              {translate('Statement Date')}
              {':'}
            </span>
            <DatesPickerDropdown
              since={moment.utc(report.since)}
              until={moment.utc(report.until)}
              minimumRangeSize={0}
              sinceLabel={translate('Start Date')}
              untilLabel={translate('Close Date')}
              sinceTooltipText={translate(
                'The statement start date of a statement is determined by the end date of the previous statement. To update the start date, please update the end date of the previous statement.',
              )}
              disabled={cyclePickerDisabled ? true : 'since'}
              loading={isPending}
              initialMonth={moment.utc(report.until)}
              isOutsideRange={(day): boolean => {
                const isAfterRange = day
                  .utc()
                  .isAfter(moment.utc().add(3, 'month'), 'day')
                if (
                  report.id === cycle?.currentReportId ||
                  report.id === cycle?.previousReportId
                ) {
                  return (
                    isAfterRange ||
                    day.utc().isBefore(moment.utc(report?.since), 'day')
                  )
                } else {
                  return true
                }
              }}
              setSince={(): void => {
                return
              }}
              setUntil={(): void => {
                return
              }}
              onProcessDatesChange={(
                since,
                until,
              ): {
                since: moment.Moment | undefined
                until: moment.Moment | undefined
              } => {
                let processedSince = since?.utc()
                let processedUntil = until?.utc()
                // if until is null, user selected a date before since
                if (!until) {
                  processedSince = previousReport
                    ? moment.utc(previousReport.since)
                    : since
                  processedUntil = since
                } else if (until && report && until.isAfter(report.since)) {
                  processedSince = moment.utc(report.since)
                  processedUntil = until
                }
                return { since: processedSince, until: processedUntil }
              }}
              onApplyDates={(_, until): void => {
                setUpdatedUntilForPatch(until?.utc().format('YYYY-MM-DD'))
                if (
                  moment.utc(report.until).isBefore(moment.utc(), 'day') &&
                  until?.isAfter(moment.utc(), 'day')
                ) {
                  // past report, being updated to future date
                  dispatch(
                    createSetConfirmOrgExpenseReportDateChangePopup({
                      type: OrgExpenseReportConfirmDateChangePopup.MergeReports,
                      date: until,
                      rcnName: report.accountName,
                    }),
                  )
                } else if (
                  moment.utc(report.until).isSameOrAfter(moment.utc(), 'day') &&
                  until?.isBefore(moment.utc(), 'day')
                ) {
                  // current report, being updated to past date
                  dispatch(
                    createSetConfirmOrgExpenseReportDateChangePopup({
                      type: OrgExpenseReportConfirmDateChangePopup.CreateNewReport,
                      date: until,
                      rcnName: report.accountName,
                    }),
                  )
                } else {
                  // regular update, does not require confirmation modal
                  dispatch(createSetOrgExpenseReportDateUpdateConfirmed(true))
                }
              }}
              onDropdownOpen={(): void => {
                setCheckedSecondReportCycleDate([
                  ...checkedSecondReportCycleDate,
                  cycle?.id || '',
                ])
              }}
              onDropdownClose={(): void => {
                setUpdatedUntilForPatch(undefined)
                reset()
              }}
              errorMessage={error?.error}
              footerContent={
                <HoverableLink
                  to={
                    organization?.id
                      ? expenseSettingsUrl(organization?.id)
                      : location.pathname
                  }
                  ariaLabelSource={''}
                  regular
                  size={14}
                  css={css`
                    display: flex;
                    gap: 8px;
                    align-items: center;
                  `}
                >
                  <SvgSettings />
                  {translate('Statement Cycle Settings')}
                </HoverableLink>
              }
              utc={true}
              keepDropdownOpen={
                isPending || !!error?.error || !!updatedUntilForPatch
              }
              buttonMode
            />
          </div>
          {showCheckReportDateReminder && (
            <Spotlight
              tooltip
              subtitle={translate(
                'Make sure your report dates align with your credit card statement. You can update these dates at any time.',
              )}
              cookieFunction={(): void =>
                setCheckedSecondReportCycleDate([
                  ...checkedSecondReportCycleDate,
                  cycle?.id || '',
                ])
              }
              css={css`
                position: absolute;
                z-index: 3;
                width: 320px;
                margin-top: 116px;
                margin-left: 80px;
              `}
            />
          )}
        </div>
      ) : undefined}
    </>
  )
}

export default StatementSummaryHeader
